import { useContext, useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CommentIcon from "@mui/icons-material/Comment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ReportEditContext } from "../contexts/reportEdit";

// Inline editor for a single verbatim comment (local state -> commit on blur,
// so typing doesn't re-render the whole report).
const VerbatimEditField = ({ id, defaultText }) => {
  const { getVerbatim, setVerbatim } = useContext(ReportEditContext);
  const committed = getVerbatim(id, defaultText);
  const [local, setLocal] = useState(committed);
  useEffect(() => {
    setLocal(committed);
  }, [committed]);
  return (
    <TextField
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => setVerbatim(id, local, defaultText)}
      fullWidth
      multiline
      minRows={1}
      size="small"
      variant="outlined"
      sx={{ backgroundColor: "#fffbe6" }}
    />
  );
};

// Renders the open-ended ("verbatim") comments. For admins in edit mode it shows
// every comment with a show/hide toggle and an inline editor; otherwise it shows
// the curated set (hidden comments dropped, reworded text applied). For non-admin
// viewers the server already curated the list, so applying the overlay here is a
// no-op (idempotent) and keeps the admin preview consistent with the client view.
const VerbatimComments = ({ items = [] }) => {
  const { canEdit, editing, isHidden, toggleHidden, getVerbatim } =
    useContext(ReportEditContext);
  const adminEditing = canEdit && editing;

  if (adminEditing) {
    return (
      <List>
        {items.length === 0 && (
          <Typography sx={{ pl: 2 }} color="text.secondary">
            No comments submitted.
          </Typography>
        )}
        {items.map((a) => {
          const id = a._id;
          const hidden = isHidden(id);
          return (
            <ListItem
              key={id}
              alignItems="flex-start"
              sx={{ opacity: hidden ? 0.45 : 1, gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                <Tooltip
                  title={
                    hidden
                      ? "Hidden from the client report — click to show"
                      : "Shown to the client — click to hide"
                  }
                >
                  <IconButton onClick={() => toggleHidden(id)} size="small">
                    {hidden ? (
                      <VisibilityOffIcon color="error" />
                    ) : (
                      <VisibilityIcon color="success" />
                    )}
                  </IconButton>
                </Tooltip>
              </ListItemIcon>
              <VerbatimEditField id={id} defaultText={a.answer} />
            </ListItem>
          );
        })}
      </List>
    );
  }

  const visible = items.filter((a) => !isHidden(a._id));
  return (
    <List>
      {visible.map((a, i) => (
        <ListItem key={a._id || i}>
          <ListItemIcon>
            <CommentIcon />
          </ListItemIcon>
          <ListItemText primary={getVerbatim(a._id, a.answer)} />
        </ListItem>
      ))}
    </List>
  );
};

export default VerbatimComments;
